import clsx from "clsx";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  MdCheck,
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdOutlineNavigateBefore,
} from "react-icons/md";
import TextInput from "../../components/TextInput/TextInput";
import { useState } from "react";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import { API_ENDPOINT_REGISTER, ICON_SIZES } from "../../helpers/constants";
import ApiClient from "../../helpers/api-client";
import { showNotification } from "../../helpers/utils";
import { useTranslation } from "react-i18next";
import LoginFlowLayout from "../../layouts/LoginFlow/LoginFlowLayout";
import useMobile from "../../hooks/useMobile";
import { EButtonKind } from "src/components/Button/ButtonTypes";

const SIGN_UP_FORM_ID = "signup-form";

const RegisterAccountPage: React.FC = () => {
  const { state: routeState } = useLocation();
  const { isMobile } = useMobile();
  const [email, setEmail] = useState<string>(routeState?.email || "");
  const [password, setPassword] = useState<string>(routeState?.password || "");
  const [passwordConfirm, setPasswordConfirm] = useState<string>("");
  const [isRegistering, setIsRegistering] = useState<boolean>(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
  const [acceptPrivacyPolicy, setAcceptPrivacyPolicy] =
    useState<boolean>(false);

  const [name, setName] = useState<string>("");
  const acceptedAll = acceptTerms && acceptPrivacyPolicy;

  const addUser = async () => {
    setIsRegistering(true);

    return await ApiClient.post(API_ENDPOINT_REGISTER, {
      password,
      email,
      name,
      picture: "",
    })
      .then(({ data }) => {
        showNotification({
          message: t("register_page.notifications.account_created"),
          kind: "success",
        });

        navigate("/", {
          state: {
            email,
            password,
          },
        });
      })
      .catch(({ response }) => {
        showNotification({
          message: response?.data?.message,
          kind: "error",
        });
      })
      .finally(() => {
        setIsRegistering(false);
      });
  };

  return (
    <>
      <LoginFlowLayout
        title={t("register_page.title")}
        description={t("register_page.description")}
      >
        <form
          className={clsx(["flex", "flex-col", "gap-6", "w-full"])}
          id={SIGN_UP_FORM_ID}
          onSubmit={(e: any) => {
            e?.preventDefault?.();
            if (password?.trim?.() === passwordConfirm?.trim?.()) {
              showNotification({
                message: t(
                  "Ooops, no estamos aceptando usuarios en este momento."
                ),
                kind: "error",
              });
              // await addUser();
            } else {
              showNotification({
                message: t(
                  "register_page.notifications.confirm_password_error"
                ),
                kind: "error",
              });
            }
          }}
        >
          <TextInput
            disabled={isRegistering}
            value={name}
            onChange={(e: any) => setName(e?.target?.value)}
            label={t("register_page.inputs.name.label")}
            placeholder={t("register_page.inputs.name.placeholder")}
            required
          />
          <TextInput
            disabled={isRegistering}
            autoComplete="email"
            value={email}
            onChange={(e: any) => setEmail(e?.target?.value)}
            label={t("register_page.inputs.email.label")}
            type="email"
            placeholder={t("register_page.inputs.email.placeholder")}
            required
          />
          <TextInput
            disabled={isRegistering}
            value={password}
            onChange={(e: any) => setPassword(e?.target?.value)}
            type="password"
            required
            placeholder={t("register_page.inputs.password.placeholder")}
            label={t("register_page.inputs.password.label")}
            autoComplete="new-password"
          />
          <TextInput
            disabled={isRegistering}
            value={passwordConfirm}
            onChange={(e: any) => setPasswordConfirm(e?.target?.value)}
            type="password"
            required
            placeholder={t("register_page.inputs.password_confirm.placeholder")}
            label={t("register_page.inputs.password_confirm.label")}
          />
          <div className={clsx(["flex", "flex-col", "gap-3"])}>
            <div className="text-sm md:pr-16">
              Para crear, mantener y utilizar una cuenta de eXpenser.net debes
              leer y aceptar los siguientes documentos (y/o cualquier otro que
              sea requerido en el futuro):
            </div>
            <ul
              className={clsx([
                "flex",
                "w-full",
                "text-sm",
                "md:text-base",
                "flex-col",
              ])}
            >
              <li
                className={clsx([
                  "inline-flex",
                  "items-center",
                  "gap-1",
                  "rounded-md",
                  "cursor-pointer",
                ])}
              >
                {acceptPrivacyPolicy ? (
                  <MdCheckBox
                    size={isMobile ? ICON_SIZES.MD : ICON_SIZES.MD}
                    className={clsx(["text-red-500 dark:text-lime-400"])}
                    onClick={() => setAcceptPrivacyPolicy(!acceptPrivacyPolicy)}
                  />
                ) : (
                  <MdCheckBoxOutlineBlank
                    size={isMobile ? ICON_SIZES.MD : ICON_SIZES.MD}
                    className={clsx(["text-red-200 dark:text-gray-500"])}
                    onClick={() => setAcceptPrivacyPolicy(!acceptPrivacyPolicy)}
                  />
                )}
                <div>
                  <Link
                    to="/privacy-policy"
                    className={clsx([
                      "inline-flex",
                      "relative",
                      "after:content-['']",
                      "after:bottom-1",
                      "after:left-0",
                      "after:right-0",
                      "after:absolute",
                      "after:h-[1px]",
                      "after:bg-red-200",
                      "hover:after:bg-red-500",
                      "dark:after:bg-gray-500",
                      "hover:dark:after:bg-lime-400",
                      "pb-1",
                    ])}
                  >
                    Política de Privacidad
                  </Link>
                </div>
              </li>
              <li
                className={clsx([
                  "inline-flex",
                  "items-center",
                  "gap-1",
                  "rounded-md",
                  "py-1",
                  "cursor-pointer",
                ])}
              >
                {acceptTerms ? (
                  <MdCheckBox
                    size={isMobile ? ICON_SIZES.MD : ICON_SIZES.MD}
                    className={clsx(["text-red-500 dark:text-lime-400"])}
                    onClick={() => setAcceptTerms(!acceptTerms)}
                  />
                ) : (
                  <MdCheckBoxOutlineBlank
                    size={isMobile ? ICON_SIZES.MD : ICON_SIZES.MD}
                    className={clsx(["text-red-200 dark:text-gray-500"])}
                    onClick={() => setAcceptTerms(!acceptTerms)}
                  />
                )}
                <div>
                  <Link
                    to="/terms-and-conditions"
                    className={clsx([
                      "inline-flex",
                      "relative",
                      "after:content-['']",
                      "after:bottom-1",
                      "after:left-0",
                      "after:right-0",
                      "after:absolute",
                      "after:h-[1px]",
                      "after:bg-red-200",
                      "hover:after:bg-red-500",
                      "dark:after:bg-gray-500",
                      "hover:dark:after:bg-lime-400",
                      "pb-1",
                    ])}
                  >
                    Términos y Condiciones de Uso
                  </Link>
                </div>
              </li>
            </ul>
          </div>
          {/* <ButtonGroup
              buttons={[
                {
                  onClick: async () => {
                    const applePayMethod = {
                      supportedMethods: "https://apple.com/apple-pay",
                      data: {
                        version: 3,
                        merchantIdentifier: "ui.expenser.net",
                        merchantCapabilities: [
                          "supports3DS",
                          "supportsCredit",
                          "supportsDebit",
                        ],
                        supportedNetworks: [
                          "amex",
                          "discover",
                          "masterCard",
                          "visa",
                          "maestro",
                        ],
                        countryCode: "US",
                      },
                    };

                    const cardMethod = {
                      supportedMethods: "basic-card",
                      data: {
                        supportedNetworks: ["visa", "mastercard"],
                      },
                    };

                    const paymentDetails = {
                      id: "order-123",
                      displayItems: [
                        {
                          label: "PWA Demo Payment",
                          amount: { currency: "USD", value: "0.01" },
                        },
                      ],
                      total: {
                        label: "Total",
                        amount: { currency: "USD", value: "0.01" },
                      },
                    };

                    // const request = new PaymentRequest(
                    //   [applePayMethod],
                    //   paymentDetails
                    // );
                    // const response = await request.show();
                    const request = new PaymentRequest(
                      [cardMethod],
                      paymentDetails
                    );
                    const response = await request.show();

                    console.log(response);
                  },
                  text: "Tarjeta",
                  icon: <MdPayment size={32} />,
                },
                {
                  onClick: async () => {
                    const applePayMethod = {
                      supportedMethods: "https://apple.com/apple-pay",
                      data: {
                        version: 3,
                        merchantIdentifier: "ui.expenser.net",
                        merchantCapabilities: [
                          "supports3DS",
                          "supportsCredit",
                          "supportsDebit",
                        ],
                        supportedNetworks: [
                          "amex",
                          "discover",
                          "masterCard",
                          "visa",
                          "maestro",
                        ],
                        countryCode: "US",
                      },
                    };

                    const cardMethod = {
                      supportedMethods: "basic-card",
                      data: {
                        supportedNetworks: ["visa", "mastercard"],
                      },
                    };

                    const paymentDetails = {
                      id: "expenser-order-123",
                      displayItems: [
                        {
                          label: "Membresía anual",
                          amount: { currency: "USD", value: "120.00" },
                        },
                      ],
                      total: {
                        label: "Total",
                        amount: { currency: "USD", value: "120.00" },
                      },
                    };

                    const request = new PaymentRequest(
                      [applePayMethod],
                      paymentDetails
                    );
                    const response = await request.show();

                    console.log(response);
                  },
                  text: "Apple",
                  icon: <MdPayment size={32} />,
                },
              ]}
              gap={1}
            /> */}

          <div
            className={clsx([
              "w-full",
              "pt-4",
              "flex",
              "justify-between",
              "flex-row",
            ])}
          >
            <ButtonGroup
              gap={1}
              buttons={[
                {
                  disabled: isRegistering,
                  type: "button",
                  onClick: () => {
                    navigate("/", {
                      state: {
                        email,
                        password,
                      },
                    });
                  },
                  icon: <MdOutlineNavigateBefore size={ICON_SIZES.MD} />,
                  kind: EButtonKind.NORMAL,
                },
              ]}
              align="left"
            />
            <ButtonGroup
              gap={1}
              buttons={[
                {
                  disabled: isRegistering || !acceptedAll || true,
                  kind: EButtonKind.SAVE,
                  text: t("register_page.buttons.register"),
                  isLoading: isRegistering,
                  onClick: () => {},
                  icon: <MdCheck size={ICON_SIZES.MD} />,
                  form: SIGN_UP_FORM_ID,
                  type: "submit",
                },
              ]}
              align="right"
            />
          </div>
        </form>
      </LoginFlowLayout>
    </>
  );
};

export default RegisterAccountPage;
